import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
export function GuiaMedicare() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const goToContact = () => {
    navigate('/');
    const tryScroll = (attempts: number) => {
      const el = document.getElementById('contacto');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
      } else if (attempts > 0) {
        setTimeout(() => tryScroll(attempts - 1), 200);
      }
    };
    setTimeout(() => tryScroll(15), 100);
  };
  const sections = [t('Partes', 'Parts'), t('Opciones', 'Options'), t('Comparar', 'Compare'), t('Inscripción', 'Enrollment'), t('Errores', 'Mistakes'), t('Checklist', 'Checklist')];
  const parts = [{
    id: 'A',
    label: t('Parte A', 'Part A'),
    covers: t('Hospitalizaciones, centro de enfermería especializada, hospice y cuidado en el hogar.', 'Hospital stays, skilled nursing facility care, hospice, and home health care.'),
    note: t('A muchas personas no les cuesta prima mensual porque ya pagaron impuestos de Medicare.', 'Most people do not pay a monthly premium because they already paid Medicare taxes.'),
    medigap: false
  }, {
    id: 'B',
    label: t('Parte B', 'Part B'),
    covers: t('Servicios médicos, consultas, cuidado ambulatorio, equipo médico duradero y servicios preventivos.', 'Medical services, doctor visits, outpatient care, durable medical equipment, and preventive services.'),
    note: t('Normalmente tiene prima mensual. Se necesita junto con la Parte A para muchas opciones.', 'Usually has a monthly premium. Needed along with Part A for many options.'),
    medigap: false
  }, {
    id: 'C',
    label: t('Parte C', 'Part C'),
    covers: t('Medicare Advantage. Forma privada de recibir tu cobertura de Medicare.', 'Medicare Advantage. A private way to get your Medicare coverage.'),
    note: t('Incluye Parte A y B, y usualmente Parte D. Puede incluir extras, pero suele trabajar con red.', 'Includes Part A and B, and usually Part D. May include extras, but usually works with a network.'),
    medigap: false
  }, {
    id: 'D',
    label: t('Parte D', 'Part D'),
    covers: t('Medicamentos recetados.', 'Prescription drugs.'),
    note: t('Se obtiene con un plan separado o incluido dentro de muchos planes Medicare Advantage.', 'Obtained with a separate plan or included in many Medicare Advantage plans.'),
    medigap: false
  }, {
    id: 'Medigap',
    label: 'Medigap',
    covers: t('Seguro suplementario para ayudar con gastos de tu bolsillo en Original Medicare.', 'Supplemental insurance to help with out-of-pocket costs in Original Medicare.'),
    note: t('Solo se usa con Original Medicare. No se combina con Medicare Advantage.', 'Only used with Original Medicare. Cannot be combined with Medicare Advantage.'),
    medigap: true
  }];
  const compareRows = [[t('Libertad de proveedores', 'Provider freedom'), t('Alta – cualquier doctor que acepte Medicare', 'High – any doctor who accepts Medicare'), t('Limitada – red del plan', 'Limited – plan network')], [t('Medicamentos (Parte D)', 'Drugs (Part D)'), t('Separado – necesitas un plan D', 'Separate – you need a Part D plan'), t('Incluido en muchos planes', 'Included in many plans')], ['Medigap', t('✓ Compatible', '✓ Compatible'), t('✗ No compatible', '✗ Not compatible')], [t('Beneficios extra', 'Extra benefits'), t('No automáticos', 'Not automatic'), t('Dental, visión, audición, gimnasio, etc.', 'Dental, vision, hearing, gym, etc.')], [t('Autorización previa', 'Prior authorization'), t('Generalmente no requerida', 'Generally not required'), t('Algunos servicios la requieren', 'Some services require it')], [t('Costo mensual', 'Monthly cost'), t('Prima B + plan D + Medigap', 'Part B premium + Part D plan + Medigap'), t('Puede ser $0 adicional o baja prima', 'Can be $0 additional or low premium')]];
  const timeline = [{
    period: t('Primera elegibilidad', 'First eligibility'),
    title: t('Inscripción Inicial', 'Initial Enrollment'),
    desc: t('Tu primera gran ventana para entrar a Medicare y unirte a un plan, según tu situación.', 'Your first big window to join Medicare and enroll in a plan, depending on your situation.')
  }, {
    period: t('1 ene – 31 mar', 'Jan 1 – Mar 31'),
    title: t('Inscripción General', 'General Enrollment'),
    desc: t('Para inscribirte en Parte B y, en ciertos casos, Parte A de pago, si no lo hiciste antes.', "To enroll in Part B and, in some cases, premium Part A, if you didn't do it before.")
  }, {
    period: t('15 oct – 7 dic', 'Oct 15 – Dec 7'),
    title: t('Open Enrollment', 'Open Enrollment'),
    desc: t('Puedes cambiar, salir o entrar a planes Medicare Advantage y Parte D. Cambios efectivos el 1 de enero.', 'You can change, drop, or join Medicare Advantage and Part D plans. Changes take effect January 1.')
  }, {
    period: t('1 ene – 31 mar', 'Jan 1 – Mar 31'),
    title: t('MA Open Enrollment', 'MA Open Enrollment'),
    desc: t('Si ya tienes Medicare Advantage, puedes cambiar a otro plan MA o volver a Original Medicare.', 'If you already have Medicare Advantage, you can switch to another MA plan or return to Original Medicare.')
  }, {
    period: t('Depende del caso', 'Depends on the case'),
    title: t('Período Especial', 'Special Enrollment Period'),
    desc: t('Aplica en mudanza, pérdida de cobertura, Medicaid u otras circunstancias especiales.', 'Applies for moving, loss of coverage, Medicaid, or other special circumstances.')
  }];
  const errors = [t('Escoger un plan sin revisar si tus doctores y hospitales participan.', 'Choosing a plan without checking if your doctors and hospitals participate.'), t('No revisar si tus medicamentos están cubiertos en el formulario.', 'Not checking if your medications are covered on the formulary.'), t('Mirar solo la prima mensual y no el gasto total posible.', 'Looking only at the monthly premium and not the total possible cost.'), t('Pensar que todos los planes del mismo nombre funcionan igual.', 'Thinking all plans with the same name work the same way.'), t('No revisar el plan cada año durante Open Enrollment.', 'Not reviewing the plan every year during Open Enrollment.'), t('Confundir Medigap con Medicare Advantage.', 'Confusing Medigap with Medicare Advantage.')];
  const checkItems = [t('Tengo una lista de mis doctores, especialistas y hospitales favoritos.', 'I have a list of my favorite doctors, specialists, and hospitals.'), t('Tengo una lista completa de mis medicamentos actuales.', 'I have a complete list of my current medications.'), t('Ya comparé costos mensuales y costos cuando uso servicios.', 'I have compared monthly costs and costs when using services.'), t('Ya confirmé si necesito red amplia o más libertad para elegir proveedores.', 'I have confirmed if I need a broad network or more freedom to choose providers.'), t('Ya revisé si quiero beneficios extra como dental o visión.', 'I have checked if I want extra benefits like dental or vision.'), t('Ya confirmé si me conviene Original Medicare o Medicare Advantage.', 'I have confirmed whether Original Medicare or Medicare Advantage is better for me.'), t('Ya verifiqué si puedo aplicar a ayuda adicional o Medicaid.', 'I have verified if I can apply for extra help or Medicaid.')];
  const [checked, setChecked] = useState(Array(checkItems.length).fill(false));
  const toggle = (i: number) => setChecked(prev => prev.map((v, idx) => idx === i ? !v : v));
  const done = checked.filter(Boolean).length;
  const sectionIds = ['partes', 'opciones', 'comparar', 'inscripcion', 'errores', 'checklist'];
  const scrollTo = (idx: number) => {
    setActiveTab(idx);
    document.getElementById(sectionIds[idx])?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="min-h-screen bg-[#faf7f0] font-['DM_Sans',sans-serif] text-[#1a365d]" data-id="element-191">
      {/* Header */}
      <div className="bg-[#1a365d] text-white py-4 px-6 md:px-12 flex items-center" data-id="element-192">
        <button onClick={goToContact} className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm font-medium" data-id="element-193">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-194" />
          {t('Volver al inicio', 'Back to home')}
        </button>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1a365d] via-[#1e40af] to-[#3b82f6] px-6 md:px-10 py-16 md:py-24" data-id="element-195">
        {/* Decorative circles */}
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-[#d97706]/20 blur-2xl" data-id="element-196"></div>
        <div className="absolute -bottom-20 left-[10%] w-52 h-52 rounded-full bg-[#38bdf8]/20 blur-2xl" data-id="element-197"></div>

        <div className="relative z-10 max-w-4xl mx-auto" data-id="element-198">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-199">
            <div className="inline-block bg-[#d97706]/20 border border-[#d97706] text-[#d97706] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6" data-id="element-200">
              {t('Guía Educativa 2026', 'Educational Guide 2026')}
            </div>
            <h1 className="font-['Playfair_Display',serif] text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 max-w-2xl" data-id="element-201">
              {t('Guía Básica de ', 'Basic Guide to ')}
              <span className="text-[#d97706]" data-id="element-202">Medicare</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-xl leading-relaxed mb-8" data-id="element-203">
              {t('Todo lo que necesitas saber sobre las partes de Medicare y cómo elegir el plan adecuado, explicado en lenguaje claro y fácil de entender.', 'Everything you need to know about the parts of Medicare and how to choose the right plan, explained in clear and easy-to-understand language.')}
            </p>
            <button onClick={goToContact} className="inline-flex items-center gap-2 bg-[#d97706] text-white px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-[#b45309] transition-all transform hover:scale-105 shadow-lg mb-8" data-id="element-204">
              <CalendarIcon className="w-5 h-5" data-id="element-205" />
              {t('Agenda tu cita', 'Schedule your appointment')}
            </button>
            <div className="flex items-start gap-3 bg-white/10 border-l-4 border-[#d97706] p-4 rounded-r-lg max-w-xl text-sm text-white/90" data-id="element-206">
              <span className="text-xl" data-id="element-207">ℹ️</span>
              <p data-id="element-208">
                {t('Esta guía resume reglas generales de Medicare vigentes en 2026. Los costos, beneficios y redes pueden variar según el plan y el condado.', 'This guide summarizes general Medicare rules in effect for 2026. Costs, benefits, and networks may vary by plan and county.')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 md:px-10 shadow-sm" data-id="element-209">
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto no-scrollbar py-1" data-id="element-210">
          {sections.map((s, i) => <button key={s} className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === i ? 'text-[#1e40af] border-[#1e40af]' : 'text-gray-500 border-transparent hover:text-[#1a365d]'}`} onClick={() => scrollTo(i)} data-id="element-211">
              {s}
            </button>)}
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-id="element-212">
        {/* PARTES */}
        <section id="partes" className="py-12 scroll-mt-20" data-id="element-213">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-214"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-215">
            {t('Las Partes de Medicare', 'The Parts of Medicare')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-216">
            {t('Medicare no es un solo plan: es un sistema con varias partes y opciones de cobertura.', 'Medicare is not a single plan: it is a system with several parts and coverage options.')}
          </p>

          <div className="grid gap-4" data-id="element-217">
            {parts.map((p, i) => <motion.div key={p.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`bg-white rounded-xl p-6 md:p-8 border-l-4 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-6 items-start ${p.medigap ? 'border-[#d97706]' : 'border-[#1e40af]'}`} data-id="element-218">
                <div className={`font-['Playfair_Display',serif] text-2xl font-black rounded-lg px-4 py-3 text-center min-w-[80px] ${p.medigap ? 'text-[#d97706] bg-[#fefce8]' : 'text-[#1e40af] bg-blue-50'}`} data-id="element-219">
                  {p.id}
                </div>
                <div className="flex-1" data-id="element-220">
                  <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-1" data-id="element-221">
                    {t('Qué cubre', 'What it covers')}
                  </div>
                  <p className="text-gray-800 font-medium mb-3" data-id="element-222">{p.covers}</p>
                </div>
                <div className="flex-1 bg-gray-50 p-4 rounded-lg text-sm text-[#1a365d]" data-id="element-223">
                  {p.note}
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* OPCIONES */}
        <section id="opciones" className="py-12 scroll-mt-20" data-id="element-224">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-225"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-226">
            {t('Tus Dos Caminos Principales', 'Your Two Main Paths')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-227">
            {t('Elige la opción que mejor se adapte a tus necesidades y estilo de vida.', 'Choose the option that best fits your needs and lifestyle.')}
          </p>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-228">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-229">
              <div className="bg-[#1a365d] text-white p-6 md:p-8" data-id="element-230">
                <h3 className="font-['Playfair_Display',serif] text-2xl font-bold mb-1" data-id="element-231">
                  Original Medicare
                </h3>
                <p className="text-sm text-white/70" data-id="element-232">
                  {t('Parte A + Parte B + (opcional) Parte D + Medigap', 'Part A + Part B + (optional) Part D + Medigap')}
                </p>
              </div>
              <div className="p-6 md:p-8" data-id="element-233">
                <ul className="space-y-4 mb-6" data-id="element-234">
                  {[t('Más libertad para escoger doctores y hospitales que acepten Medicare.', 'More freedom to choose doctors and hospitals that accept Medicare.'), t('Puedes agregar Medigap para cubrir parte de tus gastos de bolsillo.', 'You can add Medigap to cover part of your out-of-pocket costs.'), t('Parte D se contrata por separado para cubrir medicamentos.', 'Part D is purchased separately to cover prescription drugs.'), t('Funciona en cualquier lugar de EE.UU. donde acepten Medicare.', 'Works anywhere in the U.S. where Medicare is accepted.')].map((item, i) => <li key={i} className="flex gap-3 text-sm text-gray-700" data-id="element-235">
                      <CheckIcon className="w-5 h-5 text-[#38bdf8] shrink-0" data-id="element-236" />
                      <span data-id="element-237">{item}</span>
                    </li>)}
                </ul>
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600 italic" data-id="element-238">
                  {t('Ideal si tu prioridad es libertad de elección y quieres protección adicional con Medigap.', 'Ideal if your priority is freedom of choice and you want additional protection with Medigap.')}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-239">
              <div className="bg-[#1e40af] text-white p-6 md:p-8" data-id="element-240">
                <h3 className="font-['Playfair_Display',serif] text-2xl font-bold mb-1" data-id="element-241">
                  Medicare Advantage
                </h3>
                <p className="text-sm text-white/70" data-id="element-242">
                  {t('Parte C — todo en uno, ofrecido por compañías privadas', 'Part C — all-in-one, offered by private companies')}
                </p>
              </div>
              <div className="p-6 md:p-8" data-id="element-243">
                <ul className="space-y-4 mb-6" data-id="element-244">
                  {[t('Incluye Parte A, Parte B y usualmente Parte D en un solo plan.', 'Includes Part A, Part B, and usually Part D in a single plan.'), t('Puede incluir extras como dental, visión, audición o gimnasio.', 'May include extras like dental, vision, hearing, or gym.'), t('Suele requerir usar proveedores dentro de la red del plan.', "Usually requires using providers within the plan's network."), t('Algunos servicios pueden requerir autorización previa.', 'Some services may require prior authorization.')].map((item, i) => <li key={i} className="flex gap-3 text-sm text-gray-700" data-id="element-245">
                      <CheckIcon className="w-5 h-5 text-[#38bdf8] shrink-0" data-id="element-246" />
                      <span data-id="element-247">{item}</span>
                    </li>)}
                </ul>
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600 italic" data-id="element-248">
                  {t('Ideal si prefieres un plan integrado y aceptas trabajar con una red de proveedores.', 'Ideal if you prefer an integrated plan and are willing to work with a provider network.')}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* COMPARAR */}
        <section id="comparar" className="py-12 scroll-mt-20" data-id="element-249">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-250"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-251">
            Original Medicare vs. Medicare Advantage
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-252">
            {t('Compara las diferencias clave para tomar la mejor decisión.', 'Compare the key differences to make the best decision.')}
          </p>

          <div className="overflow-x-auto" data-id="element-253">
            <table className="w-full bg-white rounded-xl overflow-hidden shadow-sm text-left border-collapse min-w-[600px]" data-id="element-254">
              <thead data-id="element-255">
                <tr className="bg-[#1a365d] text-white" data-id="element-256">
                  <th className="p-4 md:p-5 font-medium text-sm" data-id="element-257">
                    {t('Característica', 'Feature')}
                  </th>
                  <th className="p-4 md:p-5 font-medium text-sm" data-id="element-258">
                    Original Medicare
                  </th>
                  <th className="p-4 md:p-5 font-medium text-sm" data-id="element-259">
                    Medicare Advantage
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-260">
                {compareRows.map(([feat, orig, adv], i) => <tr key={i} className="border-b border-gray-100 last:border-0 even:bg-blue-50/30" data-id="element-261">
                    <td className="p-4 md:p-5 text-sm font-bold text-[#1a365d]" data-id="element-262">
                      {feat}
                    </td>
                    <td className="p-4 md:p-5 text-sm text-gray-700" data-id="element-263">
                      {orig.startsWith('✓') ? <span className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-264">
                          {orig}
                        </span> : orig.startsWith('✗') ? <span className="inline-block bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-265">
                          {orig}
                        </span> : orig}
                    </td>
                    <td className="p-4 md:p-5 text-sm text-gray-700" data-id="element-266">
                      {adv.startsWith('✓') ? <span className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-267">
                          {adv}
                        </span> : adv.startsWith('✗') ? <span className="inline-block bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-268">
                          {adv}
                        </span> : adv}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>

        {/* INSCRIPCIÓN */}
        <section id="inscripcion" className="py-12 scroll-mt-20" data-id="element-269">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-270"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-271">
            {t('Cuándo Puedes Inscribirte', 'When You Can Enroll')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-272">
            {t('Conoce los períodos clave para no perderte tu ventana de inscripción.', "Know the key periods so you don't miss your enrollment window.")}
          </p>

          <div className="relative pl-8 md:pl-10 border-l-2 border-gray-200 ml-4 md:ml-6 space-y-8" data-id="element-273">
            {timeline.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="relative" data-id="element-274">
                <div className="absolute -left-[41px] md:-left-[49px] top-1 w-4 h-4 rounded-full bg-[#38bdf8] border-4 border-white shadow-[0_0_0_2px_#38bdf8]" data-id="element-275"></div>
                <div className="text-xs font-bold tracking-wider uppercase text-[#38bdf8] mb-1" data-id="element-276">
                  {item.period}
                </div>
                <h4 className="text-lg font-bold text-[#1a365d] mb-2" data-id="element-277">
                  {item.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-278">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-10 bg-red-50 border border-red-200 rounded-xl p-5 md:p-6 flex gap-4 items-start" data-id="element-279">
            <span className="text-2xl" data-id="element-280">⚠️</span>
            <p className="text-sm text-red-800 leading-relaxed" data-id="element-281">
              {t('Si dejas pasar tu momento de inscripción y no calificas para un período especial, podrías pagar ', 'If you miss your enrollment window and do not qualify for a special period, you could pay ')}
              <strong className="font-bold" data-id="element-282">
                {t('penalidades por inscripción tardía', 'late enrollment penalties')}
              </strong>
              {t(', especialmente en Parte B y Parte D.', ', especially for Part B and Part D.')}
            </p>
          </div>
        </section>

        {/* ERRORES */}
        <section id="errores" className="py-12 scroll-mt-20" data-id="element-283">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-284"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-285">
            {t('Errores Comunes que Evitar', 'Common Mistakes to Avoid')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-286">
            {t('Estos son los errores más frecuentes al elegir o usar Medicare.', 'These are the most frequent mistakes when choosing or using Medicare.')}
          </p>

          <div className="grid md:grid-cols-2 gap-4" data-id="element-287">
            {errors.map((e, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-white rounded-xl p-5 md:p-6 shadow-sm border-t-4 border-red-500 relative overflow-hidden" data-id="element-288">
                <span className="absolute top-2 right-4 font-['Playfair_Display',serif] text-5xl font-black text-red-500/10 leading-none" data-id="element-289">
                  {i + 1}
                </span>
                <p className="text-sm text-gray-800 relative z-10 pr-6" data-id="element-290">{e}</p>
              </motion.div>)}
          </div>
        </section>

        {/* CHECKLIST */}
        <section id="checklist" className="py-12 scroll-mt-20" data-id="element-291">
          <div className="w-12 h-1 bg-[#d97706] rounded mb-3" data-id="element-292"></div>
          <h2 className="font-['Playfair_Display',serif] text-3xl font-bold text-[#1a365d] mb-2" data-id="element-293">
            {t('Checklist Antes de Elegir', 'Checklist Before Choosing')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-294">
            {done}/{checkItems.length}{' '}
            {t('completados — haz clic para marcar cada paso.', 'completed — click to check off each step.')}
          </p>

          <div className="space-y-3" data-id="element-295">
            {checkItems.map((item, i) => <motion.button key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} onClick={() => toggle(i)} className={`w-full text-left flex gap-4 items-start p-4 md:p-5 rounded-xl shadow-sm transition-all ${checked[i] ? 'bg-green-50 text-gray-500' : 'bg-white text-gray-800 hover:bg-gray-50'}`} data-id="element-296">
                <div className={`w-6 h-6 shrink-0 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${checked[i] ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`} data-id="element-297">
                  {checked[i] && <CheckIcon className="w-4 h-4" data-id="element-298" />}
                </div>
                <span className={`text-sm md:text-base ${checked[i] ? 'line-through' : ''}`} data-id="element-299">
                  {item}
                </span>
              </motion.button>)}
          </div>
        </section>

        {/* CTA */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="py-12 scroll-mt-20" data-id="element-300">
          <div className="bg-[#1a365d] rounded-2xl p-8 md:p-12 text-center" data-id="element-301">
            <h2 className="font-['Playfair_Display',serif] text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-302">
              {t('¿Necesitas ayuda eligiendo tu plan?', 'Need help choosing your plan?')}
            </h2>
            <p className="text-white/70 max-w-lg mx-auto mb-8 leading-relaxed" data-id="element-303">
              {t('Te asesoro sin costo para encontrar la mejor opción de Medicare según tus necesidades, médicos y presupuesto.', 'I advise you at no cost to find the best Medicare option based on your needs, doctors, and budget.')}
            </p>
            <button onClick={goToContact} className="inline-flex items-center gap-2 bg-[#d97706] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#b45309] transition-all transform hover:scale-105 shadow-lg" data-id="element-304">
              <CalendarIcon className="w-5 h-5" data-id="element-305" />
              {t('Agenda tu cita', 'Schedule your appointment')}
            </button>
          </div>
        </motion.section>
      </div>

      {/* Footer */}
      <footer className="bg-[#1a365d] text-white/60 py-10 px-6 text-center text-sm" data-id="element-306">
        <div className="max-w-3xl mx-auto space-y-4" data-id="element-307">
          <p data-id="element-308">
            {t('Fuentes:', 'Sources:')}{' '}
            <strong className="text-[#d97706]" data-id="element-309">Medicare.gov</strong> — Parts of
            Medicare, Joining a Plan, Open Enrollment, Part D, Medigap, Avoid
            Late Enrollment Penalties
          </p>
          <p data-id="element-310">
            {t('Preparado en español sencillo para orientación general. Para comparar planes específicos, usa ', 'Prepared in plain language for general guidance. To compare specific plans, use ')}
            <strong className="text-[#d97706]" data-id="element-311">Medicare.gov</strong>{' '}
            {t('o busca asesoría personalizada.', 'or seek personalized advice.')}
          </p>
        </div>
      </footer>
    </div>;
}